import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useState } from "react";
import styles from "./ContentHeroSection.module.scss";
import Image from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import Carousel from "@Components/Carousel";
import useIsDesktop from "hooks/useIsDesktop";

export interface HeroItem {
  id: string;
  title: string;
  category: string;
  readTime: string;
  publishDate: string;
  description: string;
  image: string;
  slug: string;
}

interface ContentHeroSectionProps {
  items: HeroItem[];
  basePath: string;
}

const ContentHeroSection = ({ items, basePath }: ContentHeroSectionProps) => {
  const isDesktop = useIsDesktop();
  const [activeSlide, setActiveSlide] = useState(1);

  const renderSlide = (item: HeroItem, index: number) => (
    <CustomLink
      to={`${basePath}/post/${item.slug}`}
      redirect={isDesktop}
      className={styles.heroCard}
    >
      <div className={styles.heroImage}>
        <Image
          src={`${item.image}?w=${isDesktop ? 800 : 500}`}
          alt={item.title}
          width="800"
          height="400"
          isEagerLoad={index === 0}
        />
      </div>
      <div className={styles.heroContent}>
        <div className={styles.metaRow}>
          <span className={styles.categoryBadge}>{item.category}</span>
          <div className={styles.metaInfo}>
            <span className={styles.readTime}>{item.readTime}</span>
            <span className={styles.separator}>·</span>
            <span className={styles.date}>{item.publishDate}</span>
          </div>
        </div>
        <h1 className={styles.heroTitle}>{item.title}</h1>
        <p className={styles.heroDescription}>{item.description}</p>
        <div className={styles.carouselIndicator}>
          <span className={styles.currentSlide}>
            {activeSlide}/{items.length}
          </span>
          <div className={styles.dots}>
            {items.map((_, dotIndex) => (
              <div
                key={dotIndex}
                className={`${styles.dot} ${dotIndex === activeSlide - 1 ? styles.activeDot : ""}`}
              />
            ))}
          </div>
        </div>
      </div>
    </CustomLink>
  );

  const slideList = items.map((item, index) => renderSlide(item, index));

  return (
    <div className={styles.heroSection}>
      <Carousel
        slideList={slideList}
        activePos={activeSlide}
        handleChangeSlide={setActiveSlide}
        autoSlide
        delay={8}
        showNavSlider={isDesktop}
        className={styles.carousel}
        sliderContainerClassName={styles.sliderContainer}
        sliderWrapperClassName={styles.carouselWrapper}
      />
    </div>
  );
};

export default wrapperHOC(memo(ContentHeroSection), {
  componentName: "ContentHeroSection",
  showForChina: true,
});
